import { Trans } from '@lingui/react/macro';
import { useMemo } from 'react';

import { useIntegrationProvidersLite } from '@/api/integration';
import { Button, PlainButton } from '@/components/buttons';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/DropdownMenu';
import { Spinner } from '@/components/loaders';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/Tooltip';

import type { RuleEntityAttributeType, RuleEntityType } from './types';

type Props = {
  type: 'AND' | 'OR' | 'ADD';
  ruleEntities: RuleEntityType[];
  title?: string | null;
  disabled?: boolean;
  onSelect: (ruleEntity: RuleEntityType, attribute?: RuleEntityAttributeType) => void;
};

const RuleEntityDropdown = ({ type, ruleEntities, title, disabled = false, onSelect }: Props) => {
  const { isLoading: isLoadingIntegrationProviders } = useIntegrationProvidersLite();

  const items = useMemo(() => {
    return ruleEntities.reduce<
      {
        key: string;
        icon?: RuleEntityType['icon'];
        label: string;
      }[]
    >((acc, ruleEntity) => {
      if (ruleEntity.id === 'company') {
        return acc.concat(
          ruleEntity.attributes.map((attribute) => ({
            key: `${ruleEntity.id}__${attribute.id}`,
            icon: ruleEntity.icon,
            label: attribute.name,
          })),
        );
      }

      if (!ruleEntity.hidden) {
        acc.push({
          key: ruleEntity.id,
          icon: ruleEntity.icon,
          label: ruleEntity.name,
        });
      }

      return acc;
    }, []);
  }, [ruleEntities]);

  const handleSelect = (key: string) => {
    const [ruleEntityId, attributeId] = key.split('__');

    const ruleEntity = ruleEntities.find((ruleEntity) => ruleEntity.id === ruleEntityId);
    if (!ruleEntity) {
      throw new Error(`Rule entity not found: ${ruleEntityId}`);
    }

    if (attributeId) {
      const attribute = ruleEntity.attributes.find((attribute) => attribute.id === attributeId);
      if (!attribute) {
        throw new Error(`Attribute not found: ${attributeId}`);
      }
      onSelect(ruleEntity, attribute);
      return;
    }

    onSelect(ruleEntity);
  };

  return (
    <Tooltip disableHoverablePopup open={title ? undefined : false}>
      <TooltipTrigger
        render={
          <div>
            <DropdownMenu>
              <DropdownMenuTrigger
                disabled={disabled}
                render={
                  type === 'OR' || type === 'AND' ? (
                    <PlainButton color={type === 'AND' ? 'green' : 'aqua'} size="small" isQuiet>
                      {type === 'AND' ? <Trans>+ AND</Trans> : <Trans>+ OR</Trans>}
                    </PlainButton>
                  ) : (
                    <Button className="bg-transparent!">
                      <Trans>Add Rule</Trans>
                    </Button>
                  )
                }
              />
              <DropdownMenuContent className="min-w-50">
                {isLoadingIntegrationProviders ? (
                  <DropdownMenuItem disabled>
                    <Spinner size="sm" variant="spoke" />
                    <Trans>Loading Rules...</Trans>
                  </DropdownMenuItem>
                ) : (
                  items.map((item) => (
                    <DropdownMenuItem key={item.key} onClick={() => handleSelect(item.key)}>
                      {item.icon}
                      {item.label}
                    </DropdownMenuItem>
                  ))
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        }
      />
      <TooltipContent side="left">{title}</TooltipContent>
    </Tooltip>
  );
};

export default RuleEntityDropdown;
